'use client'

import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { ProposalTrade } from '@/components/molecules/proposal-account-details'

import { useCallback, useEffect, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Dialog from '@/components/atomic/dialog'
import { ModalContext } from '@/components/atomic/dialog/modal-context'
import { Select, type SelectOption } from '@/components/atomic/select'
import { BodyDefault } from '@/components/atomic/text'
import { parseFetchError } from '@/legacy-api/helpers/error-handling'
import { COMPONENT_IDS } from '@/portfolio/proposal-builder/component-ids'
import { useDialogInsideTableModal } from '@/portfolio/shared/hooks/use-dialog-inside-table-modal'
import { ErrorBanner } from '@/shared/components/banners'

import { DialogContentContainer, DialogFooterContainer } from './styles'

/** Stable context value — prevents the Select from using a modal Popover which
 *  sets pointer-events:none on the Dialog's DismissableLayer. */
const NON_MODAL_CONTEXT = { insideModal: false } as const

export interface RemoveDialogOption {
  label: string
  value: string
}

interface ConfirmProposalOrderRemoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (selectedOption?: string) => Promise<void>
  tradeDetails: ProposalTrade
  options?: RemoveDialogOption[]
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ConfirmProposalOrderRemoveDialog({
  open,
  onOpenChange,
  onConfirm,
  tradeDetails,
  options,
}: ConfirmProposalOrderRemoveDialogProps) {
  const [isRemoving, setIsRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined,
  )

  useEffect(() => {
    if (open) setSelectedValue(options?.[0]?.value)
  }, [open, options])

  const handleCloseDialog = useCallback(() => {
    setError(null)
    onOpenChange(false)
  }, [onOpenChange])

  const handleConfirm = useCallback(async () => {
    setIsRemoving(true)
    setError(null)
    try {
      await onConfirm(selectedValue)
      handleCloseDialog()
    } catch (error) {
      const errorMessage = parseFetchError(
        error as FetchBaseQueryError | SerializedError,
        'An unexpected error occurred. Please try again.',
      )
      setError(errorMessage)
    } finally {
      setIsRemoving(false)
    }
  }, [onConfirm, selectedValue, handleCloseDialog])

  const selectOptions: SelectOption[] = useMemo(
    () => (options ?? []).map((o) => ({ label: o.label, value: o.value })),
    [options],
  )

  const { onInteractOutside } = useDialogInsideTableModal({
    onClose: handleCloseDialog,
    preventClose: isRemoving,
  })

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onInteractOutside={onInteractOutside}
      title={`Remove allocation for "${tradeDetails.name}"?`}
      mainContent={
        <DialogContentContainer
          data-testid={COMPONENT_IDS.dialogs.confirmProposalOrderRemove.root}
        >
          {error && <ErrorBanner message={error} className="mb-4" />}
          <BodyDefault>
            This bond&apos;s allocation will be removed from the proposal. The
            allocated cash will not be invested. If you want to deploy this
            cash, generate another proposal or replace this bond, rather than
            removing it.
          </BodyDefault>
          {selectOptions.length > 0 && (
            <div className="mt-3">
              <ModalContext.Provider value={NON_MODAL_CONTEXT}>
                <Select
                  options={selectOptions}
                  value={selectedValue}
                  onValueChange={setSelectedValue}
                  disabled={isRemoving}
                  hideSearch
                />
              </ModalContext.Provider>
            </div>
          )}
        </DialogContentContainer>
      }
      footer={
        <DialogFooterContainer>
          <Button
            variant={ButtonVariant.Secondary}
            label="Go back"
            onClick={handleCloseDialog}
            disabled={isRemoving}
            data-testid={
              COMPONENT_IDS.dialogs.confirmProposalOrderRemove.cancelButton
            }
          />
          <Button
            variant={ButtonVariant.Primary}
            label={isRemoving ? 'Removing...' : 'Remove allocation'}
            onClick={handleConfirm}
            disabled={isRemoving}
            className="bg-moment-red-600 hover:bg-moment-red-700 dark:bg-moment-red-500 dark:hover:bg-moment-red-600"
            data-testid={
              COMPONENT_IDS.dialogs.confirmProposalOrderRemove.confirmButton
            }
          />
        </DialogFooterContainer>
      }
    />
  )
}
